import api, {
    deleteFn,
    IdApiArg,
    insertFn,
    PaginatedResult,
    SortColumn,
    SupabaseQueryArgs,
    UpdateArg,
    updateFn,
    UpdateResp
} from "../../api";

import {supabase} from "../../supabase";
import {Tables, TablesInsert} from "../../apitypes";
import {RegFilters} from "../analytics/registrations/reg_grid_slice";
import {GridState} from "../../libs/grid_state_reducers";

export interface RegistrationFilter {
    active?: boolean
    worker?: number
    user?: string
    operation?: number
    startDate?: string
    endDate?: string
    sort?: SortColumn[]
    pageSize: number
    page: number
}

export type RegistrationApiGet = Tables<"registration"> & {
    worker: Tables<"worker"> | null
    workTask: Tables<"workTask"> | null
    interruptLine: Tables<"interruptLine">[]
    workOrder: Tables<"workOrder"> | null
    workItem: Tables<"workItem"> | null
    machine: Tables<"machine"> | null
    product: Tables<"product"> | null
    profile: Tables<"profile"> | null
}

export interface WorkFinishApiPut {
    Quantity: number,
    ScrapQuantity: number,
    EndTime: string | null
}

export const regApi = api.injectEndpoints({
    endpoints: (build) => ({
        scanCode: build.query<Tables<"workOrder">[], ScanCodeQuery>({
            queryFn: async (args) => {
                const {data, error} = await supabase.from("workOrder")
                    .select("*")
                    .like("number", args.code)
                if (error) return {error};
                return {data}
            }
        }),
        popularOperations: build.query<Tables<"workTask">[], IdApiArg>({
            query: (params) => ({rpc: "popular_ops", params: {"workerid": params.id}})
        }),
        // Machine types
        getRegistration: build.query<PaginatedResult<RegistrationApiGet>, RegistrationFilter>({
            queryFn: async (args) => {
                let q = supabase
                    .from("registration")
                    .select("*,workOrder(*),workTask(*),interruptLine(*),worker(*),workItem(*),profile(*),machine(*),product(*)", {count: "exact"})

                if (args.active === true || args.active === false) {
                    if (args.active) {
                        q = q.is("EndTime", null)
                    } else {
                        q = q.not("EndTime", "is", null)
                    }
                }

                if (args.operation) {
                    q = q.eq("OperationID", args.operation)
                }
                if (args.user) {
                    if (args.worker) {
                        q = q.or(`WorkerID.eq.${args.worker},CreatedByID.eq.${args.user}`)
                    } else {
                        q = q.eq("CreatedByID", args.user)
                    }
                } else {
                    if (args.worker) {
                        q = q.eq("WorkerID", args.worker)
                    }
                }

                if (args.sort) {
                    args.sort.forEach(s => {
                        q = q.order(s.col, {ascending: s.dir == "asc"})
                    })
                }
                const {data, count, error} = await q.range(args.pageSize * args.page, args.pageSize * (args.page + 1));

                if (error) return {error};

                return {data: {result: data, count}}
            },
            providesTags: (result) =>
                result?.result
                    ? [...result.result.map(({id}) => ({type: 'Registration' as const, id})), 'Registration']
                    : [],
        }),

        // Machine types
        getRegList: build.query<PaginatedResult<RegistrationApiGet>, GridState & RegFilters>({
            queryFn: async (args) => {
                console.log(args)
                let q = supabase
                    .from("registration")
                    .select("*,workOrder(*),workTask(*),interruptLine(*),worker(*),workItem(*),profile(*),machine(*),product(*)", {count: "exact"})

                if (args.worker.length > 0) {
                    if (args.worker.findIndex((i) => i == null) != -1) {
                        q = q.is("WorkerID", null)
                    } else {
                        q = q.in("WorkerID", args.worker.map(i => i.id))
                    }
                }
                if (args.machine.length > 0) {
                    if (args.machine.findIndex((i) => i == null) != -1) {
                        q = q.is("MachineID", null)
                    } else {
                        q = q.in("MachineID", args.machine.map(i => i.id))
                    }
                }
                if (args.product.length > 0) {
                    if (args.product.findIndex((i) => i == null) != -1) {
                        q = q.is("ProductID", null)
                    } else {
                        q = q.in("ProductID", args.product.map(i => i.id))
                    }
                }
                if (args.operation.length > 0) {
                    if (args.operation.findIndex((i) => i == null) != -1) {
                        q = q.is("OperationID", null)
                    } else {
                        q = q.in("OperationID", args.operation.map(i => i.id))
                    }
                }
                if (args.partner.length > 0) {
                    if (args.partner.findIndex((i) => i == null) != -1) {
                        q = q.is("workOrder.buyerDocumentID", null).not("workOrder", "is", null)
                    } else {
                        q = q.in("workOrder.buyerDocumentID", args.partner.map(i => i.documentID)).not("workOrder", "is", null)
                    }
                }
                if (args.workOrder.length > 0) {
                    if (args.workOrder.findIndex((i) => i == null) != -1) {
                        q = q.is("WorkOrderID", null)
                    } else {
                        q = q.in("WorkOrderID", args.workOrder.map(i => i.id))
                    }
                }
                if (args.user.length > 0) {
                    if (args.user.findIndex((i) => i == null) != -1) {
                        q = q.is("CreatedByID", null)
                    } else {
                        q = q.in("CreatedByID", args.user.map(i => i.id))
                    }
                }

                if (args.startTime) {
                    q = q.gte("StartTime", args.startTime)
                }
                if (args.endTime) {
                    q = q.lt("EndTime", args.endTime)
                }

                q.eq("Status", "RegFinished")

                const {data, count, error} = await q.range(0, 10000);

                if (error) return {error};

                return {data: {result: data, count}}
            },
            providesTags: (result) =>
                result?.result
                    ? [...result.result.map(({id}) => ({type: 'Registration' as const, id})), 'Registration']
                    : [],
        }),
        getRegistrationById: build.query<RegistrationApiGet, IdApiArg>({
            query: (a): SupabaseQueryArgs => ({
                table: "registration",
                select: "*,workOrder(*),workTask(*),interruptLine(*),worker(*),workItem(*),profile(*),machine(*), product(*)",
                method: "GET",
                single: true,
                id: a.id
            }),
            providesTags: (result, error, arg) => {
                if (!error) {
                    return [{type: 'Registration', id: arg.id}, "Registration"]
                } else {
                    return []
                }
            }
        }),
        postRegistration: build.mutation<UpdateResp, TablesInsert<"registration">>({
            query: insertFn("registration"),
            invalidatesTags: (result,) => result ? [{type: 'Registration', id: result.id}, "Registration"] : [],
        }),
        deleteRegistrationById: build.mutation<UpdateResp, IdApiArg>({
            query: deleteFn("registration"),
            invalidatesTags: ["Registration"],
        }),
        putRegistrationById: build.mutation<UpdateResp, UpdateArg<"registration">>({
            query: updateFn("registration"),
            invalidatesTags: (result, error, arg) => !error ? ['Registration', {
                type: "Registration",
                id: arg.id
            }] : []
        }),
        postInterruptLine: build.mutation<UpdateResp, TablesInsert<"interruptLine">>({
            query: insertFn("interruptLine"),
            invalidatesTags: (result, err, args) => result ? [{
                type: 'Registration',
                id: `${args.RegistrationID}`
            }, "Registration"] : [],
        }),
        putInterruptLine: build.mutation<UpdateResp, UpdateArg<"interruptLine">>({
            query: updateFn("interruptLine"),
            invalidatesTags: (result, _, args) => result ? [{
                type: 'Registration',
                id: `${args?.id}`
            }, "Registration"] : [],
        }),
        getRegImagesById: build.query<PaginatedResult<Tables<"reg_images">>, IdApiArg>({
            queryFn: async (args) => {
                const q = supabase.from("reg_images").select("*").eq("RegistrationID", Number(args.id));
                const {data, count, error} = await q.range(0, 10000);

                if (error) return {error};

                return {data: {result: data, count}}
            },
            providesTags: (result, error, args) => {
                if (args && args.id) {
                    const id = args.id;
                    return [({type: 'RegImage' as const, id})]
                }
                return ["RegImage"]
            },
        }),
        postRegistrationImage: build.mutation<UpdateResp, TablesInsert<"reg_images">>({
            query: insertFn("reg_images"),
            invalidatesTags: (result, error, args) => {
                if (args && args.RegistrationID) {
                    const id = args.RegistrationID
                    return[({type: 'RegImage' as const, id}), "RegImage"]
                }
                return ["RegImage"]
            },
        }),
        deleteRegistrationImage: build.mutation<UpdateResp, IdApiArg & {RegistrationID: number}>({
            query: deleteFn("reg_images"),
            invalidatesTags: (result, error, args) => {
                if (args && args.RegistrationID) {
                    const id = args.RegistrationID
                    return[({type: 'RegImage' as const, id})]
                }
                return []
            }
        })
    }),
});

export type IntrRef = {
    ID: string
    Code: string
    Name: string
    Timeout: number
    StartTime: string
    EndTime: string | null
}

export type ScanCodeQuery = {
    code: string
}

export const {
    usePopularOperationsQuery,
    useGetRegistrationQuery,
    useGetRegListQuery,
    useGetRegistrationByIdQuery,
    usePostRegistrationMutation,
    usePutRegistrationByIdMutation,
    useDeleteRegistrationByIdMutation,
    usePostInterruptLineMutation,
    usePutInterruptLineMutation,
    usePostRegistrationImageMutation,
    useDeleteRegistrationImageMutation,
    useGetRegImagesByIdQuery
} = regApi;
